/* Brana 6.0.2 — shared reveal/parallax controller.
 * One reveal observer, one scroll frame queue, lifecycle-safe section instances.
 * Only measured scroll values are written as runtime CSS custom properties.
 * The collage's layout-specific entrance choreography remains in its CSS.
 */
(() => {
  'use strict';
  if (window.BranaMotion) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const compact = matchMedia('(max-width: 767.98px)');
  const roots = new Set();
  const items = new Set();
  const scenes = new Map();
  const finishTimers = new Map();
  const settleTimers = new Map();
  let observer = null;
  let mutationObserver = null;
  let frame = 0;
  let started = false;
  const canObserve = 'IntersectionObserver' in window;
  const canTranslate = window.CSS?.supports('translate', '0 1px');
  const DEFAULT_REVEAL_DURATION = 1260;
  const clamp = (v, min = -1, max = 1) => Math.max(min, Math.min(max, v));
  const collect = (scope, selector) => {
    const list = scope instanceof Element && scope.matches(selector) ? [scope] : [];
    if (scope.querySelectorAll) list.push(...scope.querySelectorAll(selector));
    return list;
  };
  const productSelector = [
    '.bm-materials__grid .product__item', '.section-main-collection .product__item',
    '.template-search-page .product__item', '[data-bm-product-grid] .product__item'
  ].join(',');
  const textSelector = [
    '[data-bm-motion-item]', '.bm-kicker', '.bm-editorial-kicker',
    'h1', 'h2', 'h3', '.rte', '.bm-page-display', '.bm-article__meta',
    '.bm-blog-card', '.bm-collection-card', '.bm-faq__item',
    'a.bm-text-link', 'a.bm-editorial-link',
    '.bm-section-title', '.bm-editorial-heading', '.bm-editorial-copy',
    '.bm-section-intro__copy', '.bm-editorial-actions',
    '.bm-home-hero__headline-wrap', '.bm-home-hero__copy > .rte',
    '.bm-home-hero__copy > .bm-text-link', '.bm-home-hero__stat',
    '.bm-material-index__intro > .rte', '.bm-material-index__row',
    '.bm-method__heading-wrap > *', '.bm-method__copy > .rte', '.bm-method__principle',
    '.bm-rich-text__body', '.bm-stat', '.bm-content-grid__item',
    '.bm-collection-index__row', '.bm-contact-page__detail',
    '.bm-image-text__media', '.bm-shoppable-story__media', '.bm-product-spotlight__media',
    '.bm-article__media', '.bm-about-intro__image'
  ].join(',');

  function reveal(node, immediately = false) {
    if (roots.has(node)) {
      node.classList.add('bm-in-view');
      settleScene(node, immediately);
    }
    if (items.has(node)) {
      // Commit the prepared starting style before applying the end state.
      // Above-the-fold items can otherwise receive both classes in one paint
      // and skip their entrance entirely (notably Contact and About).
      const animate = !immediately && !reduced.matches && canObserve;
      const style = animate ? getComputedStyle(node) : null;
      if (style) void style.opacity;
      const duration = style ? parseFloat(style.getPropertyValue('--bm-motion-duration')) || DEFAULT_REVEAL_DURATION : 0;
      const delay = style ? parseFloat(style.getPropertyValue('--bm-motion-delay')) || 0 : 0;
      node.classList.add('bm-motion-seen');
      if (!animate) {
        clearTimeout(finishTimers.get(node));
        finishTimers.delete(node);
        node.classList.add('bm-motion-settled');
      } else if (!finishTimers.has(node)) {
        finishTimers.set(node, setTimeout(() => {
          node.classList.add('bm-motion-settled');
          finishTimers.delete(node);
        }, duration + delay + 60));
      }
    }
    observer?.unobserve(node);
  }

  function watch(node) {
    if (reduced.matches || !canObserve) reveal(node, true);
    else observer.observe(node);
  }

  function prepareItem(node, sequence = 0, variant = '') {
    if (items.has(node)) return;
    items.add(node);
    node.classList.add('bm-motion-item');
    if (variant) node.classList.add(variant);
    if (!node.hasAttribute('data-bm-motion-item')) node.dataset.bmMotionItem = String(sequence % 6);
    watch(node);
  }

  function prepare(scope = document) {
    collect(scope, '[data-bm-reveal]').forEach(root => {
      if (roots.has(root)) return;
      roots.add(root);
      const candidates = [...root.querySelectorAll(textSelector)].filter(node =>
        node.closest('[data-bm-reveal]') === root && !node.closest('#CartDrawer, .header-search, .product__item'));
      const chosen = new Set(candidates);
      let sequence = 0;
      candidates.forEach(node => {
        // Never animate a text wrapper and one of its children a second time.
        for (let parent = node.parentElement; parent && parent !== root; parent = parent.parentElement) {
          if (chosen.has(parent)) return;
        }
        const role = node.matches('.bm-kicker,.bm-editorial-kicker') ? 0
          : node.matches('h1,h2,h3,.bm-section-title,.bm-editorial-heading,.bm-page-display') ? 1
          : node.matches('.rte,.bm-editorial-copy,.bm-section-intro__copy') ? 2
          : node.matches('.bm-editorial-actions,.bm-text-link') ? 3 : sequence++;
        let variant = node.matches('.bm-article__media,.bm-about-intro__image') ? 'bm-motion-fade' : '';
        if (node.matches('.bm-home-hero__headline-wrap,.bm-material-index__row,.bm-method__principle')) variant = 'bm-motion-from-left';
        if (node.matches('.bm-home-hero__copy > .rte')) variant = 'bm-motion-from-right';
        prepareItem(node, role, variant);
      });
      const feature = root.querySelector('[data-bm-feature-media]');
      if (feature) prepareItem(feature, 0, 'bm-motion-feature');
      const video = root.matches('.bm-video-story') && root.querySelector('[data-bm-scene-media]');
      if (video) prepareItem(video, 0, 'bm-motion-fade');
      if (!candidates.length && !feature && !video) prepareItem(root);
      watch(root);
    });
    collect(scope, productSelector).filter(node => !node.closest('#CartDrawer,.header-search')).forEach((node, index) => {
      const grid = node.closest('[data-bm-product-grid],#product-grid,.product-grid,.grid-cols') || node.parentElement;
      const siblings = [...grid.querySelectorAll('.product__item')];
      prepareItem(node, Math.max(0, siblings.indexOf(node)) % 4, 'bm-motion-product');
    });
    collect(scope, '.template-product .feature-product-media').forEach(node => prepareItem(node, 0, 'bm-motion-fade'));
    // Rich-text portraits use the same scheduler rather than separate listeners.
    collect(scope, '[data-bm-editorial-image-parallax]').forEach(root => settleScene(root));
  }

  function addTrack(scene, node, amplitude, axis = 'y') {
    if (!node) return;
    node.classList.add(axis === 'y' ? 'bm-motion-parallax' : 'bm-motion-rule');
    scene.tracks.push({ node, amplitude, axis });
  }

  function buildScene(root) {
    if (reduced.matches || !canTranslate || scenes.has(root) || !root.isConnected) return;
    const mobile = compact.matches;
    const kind = root.dataset.bmEditorialScene;
    const portrait = root.hasAttribute('data-bm-editorial-image-parallax');
    if (kind === 'collage' && !root.hasAttribute('data-bm-collage-parallax')) return;
    if (!portrait && !kind) return;
    if (kind === 'materials' && !root.classList.contains('bm-materials--mode-feature')) return;
    const measuredRoot = portrait ? root.parentElement : root;
    const rect = measuredRoot.getBoundingClientRect();
    const height = window.innerHeight;
    // Start at the settled entrance position. Progress then follows the whole
    // scene's passage through the viewport, not a capped fraction of one screen.
    const scene = { root, tracks: [], portrait,
      originProgress: (height - rect.top) / Math.max(1, height + rect.height) };
    const media = root.querySelector('[data-bm-scene-media]');
    const copy = root.querySelector('[data-bm-scene-copy]');
    if (portrait) {
      addTrack(scene, root, mobile ? -22 : -38);
      addTrack(scene, root.querySelector('.bm-rich-text__portrait-image'), mobile ? 18 : 32);
    } else {
      const amplitudes = { materials: mobile ? [15,-10] : [24,-16],
        collage: mobile ? [14,-8] : [22,-13], video: mobile ? [0,-18] : [0,-28] }[kind];
      if (!amplitudes) return;
      if (amplitudes[0]) addTrack(scene, media, amplitudes[0]);
      addTrack(scene, copy, amplitudes[1]);
    }
    if (kind === 'collage') {
      const layout = mobile ? 'plan' : ['interlock','axis','splice','ledger','plan']
        .find(name => root.classList.contains(`bm-raw-collage--${name}`)) || 'interlock';
      const profile = mobile ? [9,-8,7,-10,8] : {
        interlock: [-19,-22,-18,-24,-20], axis: [24,-21,23,-26,20],
        splice: [-24,21,-23,26,-20], ledger: [-19,-22,-18,-24,-20], plan: [14,-12,11,-16,13]
      }[layout];
      root.querySelectorAll('[data-bm-collage-parallax-item]').forEach((node,i) => addTrack(scene,node,profile[i%5]));
      const strengths = mobile ? [.10,.14,.09,.16,.12,.18,.10,.15,.11] : [.13,.18,.11,.20,.15,.22,.12,.19,.14];
      root.querySelectorAll('.bm-raw-collage__rule').forEach((node,i) => {
        const rect=node.getBoundingClientRect();
        addTrack(scene,node,strengths[i%9] * (i%2 ? -1 : 1),rect.width >= rect.height ? 'scale-x' : 'scale-y');
      });
      if (layout === 'interlock' || layout === 'ledger') addTrack(scene,root.querySelector('[data-bm-collage-copy]'),-15);
    }
    scenes.set(root,scene);
    schedule();
  }

  function settleScene(root, immediately = false) {
    if (reduced.matches || scenes.has(root) || settleTimers.has(root)) return;
    const kind = root.dataset.bmEditorialScene;
    if (!kind && !root.hasAttribute('data-bm-editorial-image-parallax')) return;
    let wait = 0;
    if (!immediately) {
      root.querySelectorAll('.bm-motion-item:not(.bm-motion-settled)').forEach(node => {
        const style = getComputedStyle(node);
        wait = Math.max(wait, (parseFloat(style.getPropertyValue('--bm-motion-duration')) || DEFAULT_REVEAL_DURATION)
          + (parseFloat(style.getPropertyValue('--bm-motion-delay')) || 0) + 100);
      });
      if (kind === 'collage' && root.classList.contains('bm-raw-collage--motion')) {
        wait = Math.max(wait, compact.matches ? 3450 : 2920);
      }
    }
    if (!wait) return buildScene(root);
    const finishEntrance = () => {
      settleTimers.delete(root);
      if (!root.isConnected || reduced.matches) return;
      // A caption can enter later than its section. Do not start translating
      // its layer while a visible child is still making its soft landing.
      if (root.querySelector('.bm-motion-item.bm-motion-seen:not(.bm-motion-settled)')) {
        settleTimers.set(root, setTimeout(finishEntrance, 80));
        return;
      }
      buildScene(root);
    };
    settleTimers.set(root, setTimeout(finishEntrance, wait));
  }

  function update() {
    frame=0;
    if (reduced.matches) return;
    const height=window.innerHeight;
    scenes.forEach(scene => {
      if (!scene.root.isConnected) return destroyScene(scene.root);
      const measuredRoot = scene.portrait ? scene.root.parentElement : scene.root;
      const rect=measuredRoot.getBoundingClientRect();
      // A translated caption can remain on screen after the section's box.
      // Keep updating until both the section and every tracked layer have left.
      const visible = rect.bottom >= -160 && rect.top <= height + 160;
      if (!visible && !scene.tracks.some(({node}) => {
        const bounds = node.getBoundingClientRect();
        return bounds.bottom >= -160 && bounds.top <= height + 160;
      })) return;
      const passage = (height - rect.top) / Math.max(1, height + rect.height);
      const progress = scene.portrait ? clamp(passage * 2 - 1)
        : (passage - scene.originProgress) * 2;
      scene.tracks.forEach(({node,amplitude,axis}) => {
        const property=axis === 'y' ? '--bm-parallax-y' : `--bm-parallax-${axis}`;
        const value=axis === 'y' ? `${(amplitude*progress).toFixed(3)}px` : Math.max(.5, 1+amplitude*progress).toFixed(4);
        node.style.setProperty(property,value);
      });
    });
  }
  function schedule() { if (!frame && !reduced.matches) frame=requestAnimationFrame(update); }
  function destroyScene(root) {
    clearTimeout(settleTimers.get(root));settleTimers.delete(root);
    const scene=scenes.get(root);
    scene?.tracks.forEach(({node}) => {
      ['--bm-parallax-y','--bm-parallax-scale-x','--bm-parallax-scale-y'].forEach(key=>node.style.removeProperty(key));
      node.classList.remove('bm-motion-parallax','bm-motion-rule');
    });
    scenes.delete(root);
  }
  function destroy(scope) {
    for (const root of [...scenes.keys(),...settleTimers.keys()]) if(root===scope||scope.contains?.(root)) destroyScene(root);
    for (const node of items) if(node===scope||scope.contains?.(node)) {
      observer?.unobserve(node);clearTimeout(finishTimers.get(node));finishTimers.delete(node);items.delete(node);
    }
    for (const root of roots) if(root===scope||scope.contains?.(root)) { observer?.unobserve(root);roots.delete(root); }
  }
  function showSection(scope) {
    collect(scope,'[data-bm-reveal],.bm-motion-item').forEach(node=>reveal(node,true));
  }
  function resetParallax() {
    const existing=[...scenes.keys(),...settleTimers.keys()];
    existing.forEach(destroyScene);
    if (!reduced.matches) existing.forEach(root=>settleScene(root,true));
  }
  function preferenceChanged() {
    document.body.classList.toggle('bm-motion-ready',!reduced.matches && canObserve);
    resetParallax();
    if(reduced.matches) {
      roots.forEach(node=>reveal(node,true));items.forEach(node=>reveal(node,true));
    } else {
      roots.forEach(root=>{if(root.classList.contains('bm-in-view'))settleScene(root,true);});
      collect(document,'[data-bm-editorial-image-parallax]').forEach(root=>settleScene(root,true));
    }
  }
  function start() {
    if(started||!document.body.classList.contains('bm-minimalistic'))return;
    started=true;
    if(canObserve)observer=new IntersectionObserver(entries=>{
      entries.forEach(entry=>{if(entry.isIntersecting)reveal(entry.target);});
    },{rootMargin:'0px 0px -8% 0px',threshold:.02});
    document.body.classList.toggle('bm-motion-ready',!reduced.matches&&canObserve);
    prepare();
    window.addEventListener('scroll',schedule,{passive:true});
    window.addEventListener('resize',schedule,{passive:true});
    compact.addEventListener('change',resetParallax);
    reduced.addEventListener('change',preferenceChanged);
    document.addEventListener('shopify:section:load',event=>{prepare(event.target);showSection(event.target);});
    document.addEventListener('shopify:section:unload',event=>destroy(event.target));
    ['shopify:section:select','shopify:block:select'].forEach(name=>document.addEventListener(name,event=>showSection(event.target.closest('.shopify-section')||event.target)));
    document.addEventListener('focusin',event=>{
      const target=event.target.closest?.('.bm-motion-item');if(target)reveal(target,true);
      const root=event.target.closest?.('[data-bm-reveal]');if(root)reveal(root,true);
    });
    document.addEventListener('esensera:full-search-rendered',()=>prepare(document.getElementById('MainContent')||document));
    mutationObserver=new MutationObserver(records=>{
      records.forEach(record=>{
        record.removedNodes.forEach(node=>{if(node instanceof Element&&!node.isConnected)destroy(node);});
        record.addedNodes.forEach(node=>{if(node instanceof Element&&node.isConnected)prepare(node);});
      });
    });
    mutationObserver.observe(document.getElementById('MainContent')||document.body,{subtree:true,childList:true});
  }
  window.BranaMotion={init:prepare,destroy};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
})();
